import { Injectable } from '@angular/core';
import { ConfigService } from '../config/config.service';
import { ScriptService } from '../script/script.service';
import { GeoLocationErrorHandlerService } from './geo-location-error-handler.service';
import { WebservicesService } from '../webservices/webservices.service';
import { GeocomplyCertificate } from 'src/app/interfaces/geo-interface';
import { UtilsService } from '../utils/utils.service';

declare var window: any;
declare var GCOobee: any;

@Injectable({
  providedIn: 'root'
})
export class GeoLocationSDKInitService {

  private FEATURE_GEOCOMPLY_OPERATOR_ID: string;
  private FEATURE_GEOCOMPLY_ENV_ID: string;
  private FEATURE_GEOCOMPLY_CLIENT_VERSION: string;
  private FEATURE_GEOCOMPLY_PLC_VERSION_URL: string;
  private FEATURE_GEOCOMPLY_OOBEE_VERSION_URL: string;
  private FEATURE_GEOCOMPLY_NDBS_VERSION_URL: string;
  private FEATURE_GEOCOMPLY_USENODOWNLOAD: boolean;
  private isInitialized: boolean;
  private oobeeUrl: string;
  private oobeeSDKUrl: string;
  private useNDBSSolution: boolean;
  private geoComplyClient: any;
  private initObobeeConfig: any;
  private certificates: Record<string, GeocomplyCertificate> = {}

  constructor(
    private configService: ConfigService,
    private scriptService: ScriptService,
    private utilsService: UtilsService,
    private geoLocationErrorHandlerService: GeoLocationErrorHandlerService,
    private webservices: WebservicesService
  ) {
    this.useNDBSSolution = this.configService.isFeatureON( this.configService.getConfig(), "FEATURE_GEOCOMPLY_USENODOWNLOAD", undefined);
    this.FEATURE_GEOCOMPLY_ENV_ID = this.configService.getConfig().FEATURE_GEOCOMPLY_ENV_ID;
    this.FEATURE_GEOCOMPLY_OPERATOR_ID = this.configService.getConfig().FEATURE_GEOCOMPLY_OPERATOR_ID;
    this.oobeeUrl = this.configService.getConfig().FEATURE_GEOCOMPLY_OOBEE_OOBEEURL;
    this.oobeeSDKUrl = this.configService.getConfig().FEATURE_GEOCOMPLY_SDK_OOBEEURL ?? 'https://cdn.geocomply.com/oobee/v3.5/oobee.js';
  }

  public initializeGeoComply(): Promise<boolean> {
    const platformInfo = this.utilsService.getPlatformInfo();
    switch (true) {
      case platformInfo.isMobileApp:
        return this.initializeGeoComplyForMobileApp();
      case platformInfo.isMobileWeb:
        return this.initializeGeoComplyForMobileWeb();
      case platformInfo.isDesktopWeb:
        return this.initializeGeoComplyForDesktop();
      default:
        //which app should be shown as default option
        return Promise.reject(new Error('Unknown platform'));
    }
  }

  private async initializeGeoComplyForDesktop(): Promise<boolean> {
    if (this.useNDBSSolution) {
      const jsUrl = this.configService.getConfig().FEATURE_GEOCOMPLY_NDBS_VERSION_URL ?? this.configService.getConfig().FEATURE_GEOCOMPLY_NDBS_JS;
      await this.scriptService.injectLib(jsUrl, 'application/javascript');
      return new Promise<boolean>(async (resolve, reject) => {
        if (window.GcHtml5 && window.GcHtml5.createClient) {
          this.geoComplyClient = window.GcHtml5.createClient();
          this.isInitialized = true;
          return resolve(true);
        } else {
          return reject(new Error('GeoComply client creation failed.'));
        }
      })
    } else {
      const FEATURE_GEOCOMPLY_CLIENT_VERSION: String = this.configService.getConfig().FEATURE_GEOCOMPLY_CLIENT_VERSION;
      await this.scriptService.injectLib(`https://cdn.geocomply.com/plc/${FEATURE_GEOCOMPLY_CLIENT_VERSION}/geocomply-client.min.js`, 'module');
      return await new Promise<boolean>(async (resolve, reject) => {
        console.log('inside PLC SDK INNINT')
        this.geoComplyClient = window.GeoComply.Client;
        this.geoComplyClient.on('connect', () => {
          console.log('GeoComply: Client connected with ', this.geoComplyClient.getVersion());
          this.isInitialized = true;
          resolve(true);
        })
        this.geoComplyClient.on('error', (errorCode: any, errorMessage: string) => {
          console.log('Error in PLC' + errorCode + " : " + errorMessage)
          switch (errorCode) {
            case this.geoComplyClient.CLNT_ERROR_LOCAL_SERVICE_COMMUNICATION:
              this.geoComplyClient.connect(this.FEATURE_GEOCOMPLY_OPERATOR_ID, this.FEATURE_GEOCOMPLY_ENV_ID);
              this.isInitialized = true;
              reject(false);
              break;
            case this.geoComplyClient.CLNT_ERROR_REQUEST_GEOLOCATION_IN_PROGRESS:
              console.warn('geoLocationSDKINIT:: geolocation in progress, waiting...');
              this.isInitialized = false;
              reject(false);
              break;
            case this.geoComplyClient.CLNT_ERROR_TRANSACTION_TIMEOUT:
              console.error('geoLocationSDKINIT:: Connection to GeoComply Client failed. Details: ErrCode=[CLNT_ERROR_TRANSACTION_TIMEOUT]; ErrMessage=[' + errorMessage + ']');
              this.isInitialized = false;
              reject(false);
              break;
            case this.geoComplyClient.CLNT_ERROR_WRONG_OR_MISSING_PARAMETER:
              console.error('geoLocationSDKINIT:: GeoLocation failed. Details: ErrCode=[CLNT_ERROR_WRONG_OR_MISSING_PARAMETER]; ErrMessage=[' + errorMessage + ']');
              this.isInitialized = false;
              reject(false);
              break;
            case this.geoComplyClient.CLNT_ERROR_LOCAL_SERVICE_UNAVAILABLE:
              console.log('PLC is not installed on user device');
              this.geoLocationErrorHandlerService.setGeoComplyError({
                error_code: 612,
                error_message: "PLC is not installed on user device",
                troubleshooter_message: "<troubleshooter><message retry=\"0\" rule=\"boundary\">You have denied location access. Please enable it in your browser settings.</message></troubleshooter>",
                error_details: "<error_details><boundary description=\"This user does not appear to be within an inclusion zone\" type=\"out_of_boundary\"/></error_details>",
                geolocate_in: 0,
                hint: undefined,
              });
              this.isInitialized = false;
              reject(false);
              break;
            case this.geoComplyClient.CLNT_ERROR_LOCAL_LOCAL_SERVICE_COMMUNICATION:
              console.log('PLC is not installed on user device when periodic check happens');
              this.geoLocationErrorHandlerService.setGeoComplyError({
                error_code: 613,
                error_message: "PLC is not installed on user device when periodic check happens",
                troubleshooter_message: "<troubleshooter><message retry=\"0\" rule=\"boundary\">You have denied location access. Please enable it in your browser settings.</message></troubleshooter>",
                error_details: "<error_details><boundary description=\"This user does not appear to be within an inclusion zone\" type=\"out_of_boundary\"/></error_details>",
                geolocate_in: 0,
                hint: undefined,
              });
              this.isInitialized = false;
              reject(false);
              break;
            case this.geoComplyClient.CLNT_ERROR_LOCAL_LOCAL_SERVICE_UNSUP_VER:
            case this.geoComplyClient.CLNT_ERROR_LOCAL_SERVICE_UNSUP_VER:
              console.log('PLC is not supported on user device');
              this.geoLocationErrorHandlerService.setGeoComplyError({
                error_code: 615,
                error_message: "PLC version is not supported with current geoComply SDK",
                troubleshooter_message: "<troubleshooter><message retry=\"0\" rule=\"boundary\">You have denied location access. Please enable it in your browser settings.</message></troubleshooter>",
                error_details: "<error_details><boundary description=\"This user does not appear to be within an inclusion zone\" type=\"out_of_boundary\"/></error_details>",
                geolocate_in: 0,
                hint: undefined,
              });
              this.isInitialized = false;
              reject(false);
              break;
            case this.geoComplyClient.CLNT_ERROR_LICENSE_EXPIRED:
            case this.geoComplyClient.CLNT_ERROR_INVALID_LICENSE_FORMAT:
              console.log(' The license expired - request a new license and repeat the geolocation with the same parameters.')
              this.isInitialized = false;
              reject(false);
              break;
            case this.geoComplyClient.CLNT_ERROR_SERVER_COMMUNICATION:
              console.error('geoLocationSDKINIT:: GeoLocation failed. Details: ErrCode=[CLNT_ERROR_SERVER_COMMUNICATION]; ErrMessage=[' + errorMessage + ']');
              this.isInitialized = false;
              reject(false);
              break;
            default:
              this.geoLocationErrorHandlerService.setGeoComplyError(errorCode);
              this.isInitialized = false;
              reject(false);
              break;
          }
        })
        this.geoComplyClient.on('log', (log) => {
        console.log('************GEOCOMPLY LOG INFO************', log);
        });
        this.geoComplyClient.connect(this.FEATURE_GEOCOMPLY_OPERATOR_ID, this.FEATURE_GEOCOMPLY_ENV_ID);
      })
    }
  }

  private async initializeGeoComplyForMobileWeb(): Promise<any> {
    if (this.useNDBSSolution) {
      const jsUrl = this.configService.getConfig().FEATURE_GEOCOMPLY_NDBS_VERSION_URL ?? this.configService.getConfig().FEATURE_GEOCOMPLY_NDBS_JS;
      await this.scriptService.injectLib(jsUrl, 'application/javascript');
      return new Promise<boolean>(async (resolve, reject) => {
        if (window.GcHtml5 && window.GcHtml5.createClient) {
          this.geoComplyClient = window.GcHtml5.createClient();
          this.isInitialized = true;
          return resolve(true);
        } else {
          return reject(new Error('GeoComply client creation failed.'));
        }
      })
    } else {
      await this.scriptService.injectLib(this.oobeeSDKUrl, 'application/javascript');
      return new Promise<boolean>(async (resolve, reject) => {
        this.setInitObobeeConfig();
        this.geoComplyClient = GCOobee.createClient(this.getInitObobeeConfig());
        this.isInitialized = true;
        return resolve(true);
      })

    }
  }

  //Make this method public as Android App needs to be check/initialize SDK at app launch
  public initializeGeoComplyForMobileApp(): Promise<any> {
    return new Promise<boolean>(async (resolve, reject) => {
      if (window.cordova.plugins.geoComplySdk) {
        this.geoComplyClient = window.cordova.plugins.geoComplySdk;
        this.isInitialized = true;
        return resolve(true);
      } else {
        return reject(new Error('GeoComply client creation failed.'));
      }
    })
  }

  public requestGeoComplyLicense(clientapp: string): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      const platformInfo = this.utilsService.getPlatformInfo();
      let licenseParams: any = { clientapp: clientapp };
      if (this.useNDBSSolution && !platformInfo.isMobileApp) {
        licenseParams.solution = 'ndbs';
      }
      this.webservices.get('geoComply/license', licenseParams).subscribe((resp: any) => {
        if (resp.result) {
          this.setCertificate(resp.payload);
          resolve(true);
        } else {
          reject('invalid response from license server');
        }
      });
    })
  }

  public getGeoComplyWindowClient() {
    return this.geoComplyClient;
  }

  public getCertificate(clientApp: string): GeocomplyCertificate | undefined {
    return this.certificates[clientApp];
  }

  private setCertificate(payload: { expires?: number, certificate?: string }): void {
    if (!payload.certificate) {
      console.error('Certificate data is missing.');
      return;
    }
    const certificateKey = this.configService.getClientApp();;
    this.certificates[certificateKey] = {
      expires: payload.expires,
      license: payload.certificate
    };
    console.log('Certificate added/updated:');
  }

  private setInitObobeeConfig(licenseData?) {
    this.initObobeeConfig = {
      enablePlayStorePopUp: false,
      autoOpenAppStore: false,
      autoOpenPlayStore: false,
      oobeeUrl: this.oobeeUrl,
      license: licenseData ? licenseData : '',
    }
  }

  private getInitObobeeConfig() {
    return this.initObobeeConfig;
  }
}

